/* eslint-disable no-console */
import {BarsOutlined} from '@ant-design/icons';
import {Col, Flex, Form, Radio, Row, Select} from 'antd';
import {useForm} from 'antd/es/form/Form';
import React, {useContext, useEffect, useMemo, useState} from 'react';
import {useSearchParams} from 'react-router-dom';

import {ReactComponent as ToggleMap} from '../../../../../assets/mapBlack.svg';
import {FacilityKey} from '../../../../../components/Forms/Constants/IP/FacilityKeyLabel';
import {filterSelectOptions} from '../../../../../components/Forms/Utils/commonFunctions';
import Loader from '../../../../../components/Loader/Loader';
import {ISelect} from '../../../../../interface/ISelect';
// import yearFilterArray from '../../../../../shared/YearFilterArray';
import yearFilterArray from '../../../../../shared/YearFilterArray';
import useCompanyStore from '../../../../../store/companyStore/companyDetailsStore';
import useJSONStore from '../../../../../store/StaticJson/jsonStore';
import useUserStore from '../../../../../store/userStore/userStore';
import {hasUserRole, UserRole} from '../../../../../utils/commonFunctions';
import mapAndFilter from '../../../../../utils/mapCountry';
import SelectedCountryContext from '../../../../configurator/orgFacility/Constants/SelectedCountryContext';
import {rowGutter} from '../../OrgProfile/Forms/Constants/Common';
import {
  facilitySelectKey,
  facilitySelectLabel
} from '../constant/facilitySelectConst';
import {sortYearOptions} from '../constant/sorYear';

const FacilitySelect: React.FC<{
  setSelectedYear: React.Dispatch<React.SetStateAction<string>>;
  setFacilityId: React.Dispatch<React.SetStateAction<string>>;
  selectedYear: string;
  showLeased?: boolean;
  showMap?: boolean;
  setIsListView?: React.Dispatch<React.SetStateAction<boolean>>;
  isConfigurator?: 'supplier' | 'employee' | undefined;
}> = ({
  setFacilityId,
  setSelectedYear,
  showLeased = true,
  showMap = false,
  setIsListView,
  selectedYear,
  isConfigurator = undefined
}) => {
  const [searchParam, setSearchParams] = useSearchParams();
  const [allFacilityState, setAllFacility] = useState<any[]>([]);
  const [countryState, setCountryState] = useState('');
  const [facilityTypeState, setFacilityType] = useState(0);
  const [isLeased, setIsLeased] = useState<boolean | undefined>(
    showLeased ? undefined : false
  );

  const [yearOptions, setYearOptions] = useState<any[]>([]);

  const {setSelectedCountry} = useContext(SelectedCountryContext);

  const facilityType = useJSONStore.getState().facilityType;
  const facilityDetails = useCompanyStore.getState().facilityDetails;
  const allCountry = useJSONStore.getState().country;
  const userDetails = useUserStore.getState().userDetails;

  const handleSearchParams = (event: {
    name: string;
    value: string | undefined;
  }) => {
    const {name, value} = event;
    if (value) {
      searchParam.set(name, value);
      setSearchParams(searchParam);
    } else {
      searchParam.delete(name);
      setSearchParams(searchParam);
    }
  };

  const filterFacilityDetails = useMemo(() => {
    if (facilityDetails?.[selectedYear]) {
      return facilityDetails?.[selectedYear];
    } else {
      return [];
    }
  }, [selectedYear]);

  const filterFacility = useMemo(() => {
    if (showLeased) {
      return filterFacilityDetails;
    } else {
      return filterFacilityDetails.filter((item: any) => {
        return !item?.[FacilityKey.LEASED];
      });
    }
  }, [showLeased, filterFacilityDetails]);

  const [form] = useForm();
  const {setFields, getFieldValue} = form;

  useEffect(() => {
    if (
      userDetails.facility &&
      !(
        hasUserRole(UserRole.Company_Admin) ||
        hasUserRole(UserRole.Company_user)
      ) &&
      !isConfigurator
    ) {
      setFacilityId(userDetails.facility);
      setYearOptions(yearFilterArray(facilityDetails, userDetails.facility));
    } else if (isConfigurator === 'supplier') {
      setYearOptions(yearFilterArray(facilityDetails, undefined));
    } else if (isConfigurator === 'employee') {
      setYearOptions(yearFilterArray(facilityDetails, undefined, false));
    } else {
      setYearOptions(yearFilterArray(facilityDetails, undefined));
    }
    setAllFacility(
      filterFacility.map((item: any) => {
        return {
          isLeased: item.isLeased,
          country: item.country,
          type: item.type,
          label: item.name,
          value: item._id,
          tooltip: `${item?.[FacilityKey.ADDRESS]}`
        };
      })
    );
  }, [filterFacility]);

  const leased: ISelect[] = [
    {value: true, label: 'Yes'},
    {value: false, label: 'No'}
  ];

  useEffect(() => {
    const facilityState = filterFacilityDetails.map((item: any) => {
      return {
        isLeased: item?.isLeased,
        country: item.country,
        type: item.type,
        label: item.name,
        value: item._id,
        tooltip: `${item?.[FacilityKey.ADDRESS]}`
      };
    });

    let filteredState = facilityState;

    if (countryState) {
      filteredState = filteredState.filter((item: any) => {
        return item.country === countryState;
      });
    }

    if (facilityTypeState) {
      filteredState = filteredState.filter((item: any) => {
        return item.type === facilityTypeState;
      });
    }

    if (isLeased !== undefined) {
      filteredState = filteredState.filter((item: any) => {
        return item?.isLeased === isLeased;
      });
    }

    setAllFacility(filteredState);
  }, [countryState, facilityTypeState, isLeased, filterFacilityDetails]);

  const colSpan = 4;

  return facilityType.length === 0 && allCountry.length === 0 ? (
    <Loader />
  ) : (
    <Form
      form={form}
      initialValues={{
        [facilitySelectKey?.facilityName]: searchParam.get('facility'),
        [facilitySelectKey?.year]: selectedYear
      }}
    >
      <Row
        gutter={rowGutter}
        style={{marginTop: '1rem', marginLeft: 'auto', marginRight: 'auto'}}
      >
        <Col className="gutter-row" span={colSpan}>
          <Form.Item
            name={facilitySelectKey.year}
            label="Year"
            labelCol={{span: 24}}
          >
            <Select
              placeholder="Year"
              options={sortYearOptions(yearOptions)}
              size="middle"
              onChange={(e: any) => {
                handleSearchParams({name: 'selectedYear', value: e});
                setSelectedYear(e);
                if (
                  getFieldValue(facilitySelectKey.facilityName) !== undefined
                ) {
                  setFields([
                    {
                      name: facilitySelectKey.facilityName,
                      errors: ['Please Select Facility again'],
                      value: undefined
                    }
                  ]);
                  setFacilityId('');
                  handleSearchParams({name: 'facility', value: undefined});
                }
              }}
            />
          </Form.Item>
        </Col>
        {(hasUserRole(UserRole.Company_Admin) ||
          hasUserRole(UserRole.Company_user)) && (
          <>
            <Col className="gutter-row" span={colSpan}>
              <Form.Item
                label={facilitySelectLabel.country}
                name={facilitySelectKey.country}
                labelCol={{span: 24}}
              >
                <Select
                  placeholder={facilitySelectLabel.country}
                  options={mapAndFilter(
                    filterFacility,
                    allCountry,
                    FacilityKey.COUNTRY
                  )}
                  size="middle"
                  allowClear
                  onClear={() => {
                    setCountryState('');
                    if (
                      getFieldValue(facilitySelectKey.facilityName) !==
                      undefined
                    ) {
                      setFields([
                        {
                          name: facilitySelectKey.facilityName,
                          errors: ['Please Select Facility again'],
                          value: undefined
                        }
                      ]);
                      setFacilityId('');
                      handleSearchParams({
                        name: 'facility',
                        value: undefined
                      });
                    }
                  }}
                  onChange={(e) => {
                    setCountryState(e);
                    if (
                      getFieldValue(facilitySelectKey.facilityName) !==
                      undefined
                    ) {
                      setFields([
                        {
                          name: facilitySelectKey.facilityName,
                          errors: ['Please Select Facility again'],
                          value: undefined
                        }
                      ]);
                      setFacilityId('');
                      handleSearchParams({
                        name: 'facility',
                        value: undefined
                      });
                    }
                    if (
                      getFieldValue(facilitySelectKey.facilityType) !==
                      undefined
                    ) {
                      setFields([
                        {
                          name: facilitySelectKey.facilityType,
                          value: undefined
                        }
                      ]);
                      setFacilityId('');
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={colSpan}>
              <Form.Item
                label={facilitySelectLabel.facilityType}
                name={facilitySelectKey.facilityType}
                labelCol={{span: 24}}
              >
                <Select
                  placeholder={facilitySelectLabel.facilityType}
                  options={mapAndFilter(
                    filterFacility,
                    facilityType,
                    FacilityKey.FACILITY_TYPE,
                    undefined,
                    true
                  )}
                  size="middle"
                  allowClear
                  onClear={() => {
                    setFacilityType(0);

                    if (
                      getFieldValue(facilitySelectKey.facilityName) !==
                      undefined
                    ) {
                      setFields([
                        {
                          name: facilitySelectKey.facilityName,
                          errors: ['Please Select Facility again'],
                          value: undefined
                        }
                      ]);
                      setFacilityId('');
                      handleSearchParams({
                        name: 'facility',
                        value: undefined
                      });
                    }
                  }}
                  onChange={(e) => {
                    setFacilityType(e);
                    if (
                      getFieldValue(facilitySelectKey.facilityName) !==
                      undefined
                    ) {
                      setFields([
                        {
                          name: facilitySelectKey.facilityName,
                          errors: ['Please Select Facility again'],
                          value: undefined
                        }
                      ]);
                      setFacilityId('');
                      handleSearchParams({
                        name: 'facility',
                        value: undefined
                      });
                    }
                  }}
                />
              </Form.Item>
            </Col>
            {showLeased && (
              <Col className="gutter-row" span={colSpan}>
                <Form.Item
                  label="Leased"
                  name={facilitySelectKey.isLeased}
                  labelCol={{span: 24}}
                >
                  <Select
                    placeholder="Leased"
                    options={leased}
                    size="middle"
                    allowClear
                    onClear={() => {
                      setIsLeased(undefined);
                      if (
                        getFieldValue(facilitySelectKey.facilityName) !==
                        undefined
                      ) {
                        setFields([
                          {
                            name: facilitySelectKey.facilityName,
                            errors: ['Please Select Facility again'],
                            value: undefined
                          }
                        ]);
                        setFacilityId('');
                        handleSearchParams({
                          name: 'facility',
                          value: undefined
                        });
                      }
                    }}
                    onChange={(e) => {
                      setIsLeased(e);

                      if (
                        getFieldValue(facilitySelectKey.facilityName) !==
                        undefined
                      ) {
                        setFields([
                          {
                            name: facilitySelectKey.facilityName,
                            errors: ['Please Select Facility again'],
                            value: undefined
                          }
                        ]);
                        setFacilityId('');
                        handleSearchParams({
                          name: 'facility',
                          value: undefined
                        });
                      }
                    }}
                  />
                </Form.Item>
              </Col>
            )}
            <Col className="gutter-row" span={colSpan}>
              <Form.Item
                label="Facility Name"
                name={facilitySelectKey.facilityName}
                labelCol={{span: 24}}
              >
                <Select
                  showSearch
                  placeholder="Facility Name"
                  options={allFacilityState}
                  size="middle"
                  onChange={(e) => {
                    handleSearchParams({name: 'facility', value: e});
                    setFacilityId(e);
                    setFields([
                      {
                        name: facilitySelectKey.facilityName,
                        errors: undefined
                      }
                    ]);
                    setSelectedCountry(
                      facilityDetails[selectedYear].find((item: any) => {
                        return item._id === e;
                      })?.country
                    );
                  }}
                  filterOption={filterSelectOptions}
                />
              </Form.Item>
            </Col>
          </>
        )}

        {showMap && setIsListView && (
          <Col
            className="gutter-row"
            span={8}
            style={{
              margin: '2.48rem 0rem'
            }}
          >
            <Radio.Group
              buttonStyle="solid"
              className="toggleListMapView"
              defaultValue={true}
            >
              <Radio.Button
                onClick={() => {
                  setIsListView(true);
                }}
                value={true}
                className="radioButton1"
              >
                <Flex align="center" justify="space-between" gap={5}>
                  <BarsOutlined size={13} />
                  <div>List View</div>
                </Flex>
              </Radio.Button>
              <Radio.Button
                onClick={() => {
                  setIsListView(false);
                }}
                value={false}
                className="radioButton2"
              >
                <Flex align="center" justify="space-between" gap={5}>
                  <ToggleMap size={13} />
                  <div>Map View</div>
                </Flex>
              </Radio.Button>
            </Radio.Group>
          </Col>
        )}
      </Row>
    </Form>
  );
};

export default FacilitySelect;
